import React, { useState } from 'react';
import {
  Container,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import { TrendingUp as TrendingUpIcon, AttachMoney as AttachMoneyIcon, Assessment as AssessmentIcon, ShowChart as ShowChartIcon } from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { blue, lightBlue, blueGrey } from '@mui/material/colors';

// Import the separate calculator components
import FeeCalculator from './FeeCalculator';
import ProfitabilityCalculator from './ProfitabilityCalculator';
import RetirementReadinessCalculator from './RetirementReadinessCalculator';
import RevenueForecastCalculator from './RevenueForecastCalculator';

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: blue[700],
      light: blue[300],
      dark: blue[900],
    },
    secondary: {
      main: blueGrey[600],
      light: blueGrey[400],
      dark: blueGrey[800],
    },
    background: {
      default: lightBlue[50],
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: "'Poppins', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 15,
          },
        },
      },
    },
  },
});

const calculatorOptions = [
  {
    value: 'fee-calculator',
    label: 'Fee Calculator',
    icon: <TrendingUpIcon sx={{ mr: 1 }} />,
    description: 'Calculate advisor fees with asset growth projections'
  },
  {
    value: 'profitability-calculator',
    label: 'Profitability Calculator',
    icon: <AttachMoneyIcon sx={{ mr: 1 }} />,
    description: 'Analyze plan profitability based on time spent and service tier'
  },
  {
    value: 'retirement-readiness',
    label: 'Retirement Readiness Calculator',
    icon: <AssessmentIcon sx={{ mr: 1 }} />,
    description: 'Model participant outcomes and readiness assessments'
  },
  {
    value: 'revenue-forecast',
    label: 'Revenue Forecast Calculator',
    icon: <ShowChartIcon sx={{ mr: 1 }} />,
    description: 'Estimate growth potential from rollovers and cross-sell opportunities'
  }
];

const CalculatorPage = () => {
  const [selectedCalculator, setSelectedCalculator] = useState('fee-calculator');

  const handleCalculatorChange = (event) => {
    setSelectedCalculator(event.target.value);
  };

  const renderSelectedCalculator = () => {
    switch (selectedCalculator) {
      case 'fee-calculator':
        return <FeeCalculator />;
      case 'profitability-calculator':
        return <ProfitabilityCalculator />;
      case 'retirement-readiness':
        return <RetirementReadinessCalculator />;
      case 'revenue-forecast':
        return <RevenueForecastCalculator />;
      default:
        return <FeeCalculator />;
    }
  };

  const getSelectedCalculatorInfo = () => {
    return calculatorOptions.find(option => option.value === selectedCalculator);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ paddingTop: '2rem', paddingBottom: 6 }}>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header */}
          <motion.div variants={itemVariants}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                textAlign: 'center',
                mb: 1
              }}
            >
              Financial Calculators
            </Typography>
            <Typography 
              variant="h6" 
              component="p" 
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                mb: 4
              }}
            >
              Select a calculator to help with your financial planning and analysis
            </Typography>
          </motion.div>

          {/* Calculator Selection */}
          <motion.div variants={itemVariants}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 3, 
                mb: 4, 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid #e3f2fd'
              }}
            >
              <FormControl fullWidth>
                <InputLabel id="calculator-select-label">Select Calculator</InputLabel>
                <Select
                  labelId="calculator-select-label"
                  value={selectedCalculator}
                  label="Select Calculator"
                  onChange={handleCalculatorChange}
                  sx={{
                    borderRadius: 2,
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    }
                  }}
                >
                  {calculatorOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        {option.icon}
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {option.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
          </motion.div>

          {/* Selected Calculator Display */}
          <motion.div 
            variants={itemVariants}
            key={selectedCalculator} // This will trigger re-animation when calculator changes
            initial="hidden"
            animate="visible"
          >
            <Paper 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid #e3f2fd'
              }}
            >
              {/* Calculator Header */}
              <Box 
                sx={{ 
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  color: 'white',
                  p: 3,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {getSelectedCalculatorInfo()?.icon}
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {getSelectedCalculatorInfo()?.label}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {getSelectedCalculatorInfo()?.description}
                  </Typography>
                </Box>
              </Box>

              {/* Calculator Content */}
              <Box sx={{ p: 0 }}>
                {renderSelectedCalculator()}
              </Box>
            </Paper>
          </motion.div>
        </motion.div>
      </Container>
    </ThemeProvider>
  );
};

export default CalculatorPage; 